/**
 * Router address → human venue + icon mapping.
 *
 * The backend doesn't tag swap steps with a venue label — it just
 * carries the router contract address the bundle will call. Mapping
 * that to a brand is a UI-side concern (the dashboard renders the
 * icon; the backend stays venue-agnostic).
 *
 * Known router addresses today:
 *
 *   - Uniswap Universal Router (mainnet, V4-capable):
 *       0x66a9893cc07d91d95644aedd05d03f95e1dba8af
 *   - 1inch Aggregation Router V6 (deterministic on every chain):
 *       0x111111125421ca6dc452d289314280a0f8842a65
 *   - Curve: every pool is its own router — there is no single
 *       canonical address. Anything we don't recognise falls through
 *       to "Curve" since the only other backend in the composite is
 *       `CurveSwapRouter` and its router == the pool address.
 *
 * Icons live in `/ui/public/icons/{venue}.png`. Served by the SPA
 * itself so we don't depend on the brand sites being up.
 */

export type SwapVenue = "uniswap" | "1inch" | "curve";

export interface SwapVenueInfo {
	venue: SwapVenue;
	name: string;
	iconUrl: string;
}

const UNIVERSAL_ROUTER = "0x66a9893cc07d91d95644aedd05d03f95e1dba8af";
const ONEINCH_V6_ROUTER = "0x111111125421ca6dc452d289314280a0f8842a65";

const VENUES: Record<SwapVenue, Omit<SwapVenueInfo, "venue">> = {
	uniswap: { name: "Uniswap", iconUrl: "/icons/uniswap.png" },
	"1inch": { name: "1inch", iconUrl: "/icons/1inch.png" },
	curve: { name: "Curve", iconUrl: "/icons/curve.png" },
};

export function resolveSwapVenue(routerAddress: string): SwapVenueInfo {
	const addr = routerAddress.toLowerCase();
	if (addr === UNIVERSAL_ROUTER) {
		return { venue: "uniswap", ...VENUES.uniswap };
	}
	if (addr === ONEINCH_V6_ROUTER) {
		return { venue: "1inch", ...VENUES["1inch"] };
	}
	// Unrecognised router → Curve. Today's `RoutedSwapRouter` only
	// composes UniversalRouter + Curve, so anything that isn't the
	// two known aggregator addresses is a Curve pool. If we later add
	// 0x / Odos / Paraswap routers, extend the table.
	return { venue: "curve", ...VENUES.curve };
}
